import { engine } from '@play-co/timestep-core';
import { TickerListener } from './TickerListener';

enum UPDATE_PRIORITY {
  HIGH = 25,
  NORMAL = 0,
  LOW = -25,
  UTILITY = -50,
}

const settings = {
  TARGET_FPMS: 60 / 1000,
};

export type TickerCallback<T> = (this: T, dt: number) => void;

/**
 * A Ticker class adapted from PixiJS to work with Timestep tick events.
 */
export class Ticker {
  /** The private system ticker instance  */
  private static _system: Ticker;

  /**
   * Whether or not this ticker should invoke the method
   * when a listener is added.
   */
  public autoStart = false;
  /**
   * Scalar time value from last frame to this frame.
   * This value is capped by setting {@link PIXI.Ticker#minFPS}
   * **Note:** The cap may be exceeded by scaling.
   */
  public deltaTime = 1;
  /**
   * Scaler time elapsed in milliseconds from last frame to this frame.
   * This value is capped by setting {@link PIXI.Ticker#minFPS}
   * **Note:** The cap may be exceeded by scaling.
   * If the platform supports DOMHighResTimeStamp,
   * this value will have a precision of 1 µs.
   * Defaults to target frame time
   * @default 16.66
   */
  public deltaMS: number;
  /**
   * Time elapsed in milliseconds from last frame to this frame.
   * Opposed to what the scalar {@link PIXI.Ticker#deltaTime}
   * is based, this value is neither capped nor scaled.
   * If the platform supports DOMHighResTimeStamp,
   * this value will have a precision of 1 µs.
   * Defaults to target frame time
   * @default 16.66
   */
  public elapsedMS: number;
  /**
   * The last time {@link PIXI.Ticker#update} was invoked.
   * This value is also reset internally outside of invoking
   * update, but only when a new animation frame is requested.
   * If the platform supports DOMHighResTimeStamp,
   * this value will have a precision of 1 µs.
   */
  public lastTime = -1;
  /**
   * Factor of current {@link PIXI.Ticker#deltaTime}.
   * @example
   * // Scales ticker.deltaTime to what would be
   * // the equivalent of approximately 120 FPS
   * ticker.speed = 2;
   */
  public speed = 1;
  /**
   * Whether or not this ticker has been started.
   * `true` if {@link PIXI.Ticker#start} has been called.
   * `false` if {@link PIXI.Ticker#stop} has been called.
   * While `false`, this value may change to `true` in the
   * event of {@link PIXI.Ticker#autoStart} being `true`
   * and a listener is added.
   */
  public started = false;

  /** The first listener. All new listeners added are chained on this. */
  private _head: TickerListener | null;
  /** Internal current frame request ID */
  private _requestId: number | null = null;
  /**
   * Internal value managed by minFPS property setter and getter.
   * This is the maximum allowed milliseconds between updates.
   */
  private _maxElapsedMS = 100;
  /**
   * Internal value managed by minFPS property setter and getter.
   * This is the minimum allowed milliseconds between updates.
   */
  private _minElapsedMS = 0;
  /** If enabled, deleting is disabled.*/
  private _protected = false;
  /** The last time keyframe was executed. Maintains a relatively fixed interval with the previous value. */
  private _lastFrame = -1;
  /**
   * Internal tick method bound to ticker instance.
   * This is because in early 2015, Function.bind
   * is still 60% slower in high performance scenarios.
   * Also separating frame requests from update method
   * so listeners may be called at any time and with
   * any animation API, just invoke ticker.update(time).
   * @param time - Time since last tick.
   */
  private _tick: (time: number) => void;

  constructor() {
    this._head = new TickerListener(null, null, Infinity);
    this.deltaMS = 1 / settings.TARGET_FPMS;
    this.elapsedMS = 1 / settings.TARGET_FPMS;

    let time = 0;
    this._tick = (deltaTime: number): void => {
      time += deltaTime;
      if (this.started) {
        this.update(time);
      }
    };
  }

  /**
   * Register a handler for tick events. Calls continuously unless
   * it is removed or the ticker is stopped.
   * @param fn - The listener function to be added for updates
   * @param context - The listener context
   * @param {number} [priority=PIXI.UPDATE_PRIORITY.NORMAL] - The priority for emitting
   * @returns This instance of a ticker
   */
  public add<T = TickerListener>(
    fn: TickerCallback<T>,
    context?: T,
    priority = UPDATE_PRIORITY.NORMAL,
  ): this {
    return this._addListener(new TickerListener(fn, context, priority));
  }

  /**
   * Add a handler for the tick event which is only execute once.
   * @param fn - The listener function to be added for one update
   * @param context - The listener context
   * @param {number} [priority=PIXI.UPDATE_PRIORITY.NORMAL] - The priority for emitting
   * @returns This instance of a ticker
   */
  public addOnce<T = TickerListener>(
    fn: TickerCallback<T>,
    context?: T,
    priority = UPDATE_PRIORITY.NORMAL,
  ): this {
    return this._addListener(new TickerListener(fn, context, priority, true));
  }

  /**
   * Internally adds the event handler so that it can be sorted by priority.
   * Priority allows certain handler (user, AnimatedSprite, Interaction) to be run
   * before the rendering.
   * @private
   * @param listener - Current listener being added.
   * @returns This instance of a ticker
   */
  private _addListener(listener: TickerListener): this {
    // For attaching to head
    let current = this._head?.next;
    let previous = this._head;

    // Add the first item
    if (!current && previous) {
      listener.connect(previous);
    } else if (previous) {
      // Go from highest to lowest priority
      while (current) {
        if (listener.priority > current.priority) {
          listener.connect(previous);
          break;
        }
        previous = current;
        current = current.next;
      }

      // Not yet connected
      if (!listener.previous) {
        listener.connect(previous);
      }
    }

    return this;
  }

  /**
   * Removes any handlers matching the function and context parameters.
   * If no handlers are left after removing, then it cancels the animation frame.
   * @param fn - The listener function to be removed
   * @param context - The listener context to be removed
   * @returns This instance of a ticker
   */
  public remove<T = TickerListener>(fn: TickerCallback<T>, context?: T): this {
    let listener = this._head?.next;

    while (listener) {
      // We found a match, lets remove it
      // no break to delete all possible matches
      // incase a listener was added 2+ times
      if (listener.match(fn, context)) {
        listener = listener.destroy();
      } else {
        listener = listener.next;
      }
    }

    return this;
  }

  /**
   * The number of listeners on this ticker, calculated by walking through linked list
   * @readonly
   * @member {number}
   */
  public get count(): number {
    if (!this._head) {
      return 0;
    }

    let count = 0;
    let current: TickerListener | null = this._head;

    while ((current = current?.next)) {
      count++;
    }

    return count;
  }

  /** Starts the ticker. If the ticker has listeners a new animation frame is requested at this point. */
  public start(): void {
    if (!this.started) {
      this.started = true;
      engine.subscribe('Tick', this._tick);
    }
  }

  /** Stops the ticker. If the ticker has requested an animation frame it is canceled at this point. */
  public stop(): void {
    if (this.started) {
      this.started = false;
      engine.unsubscribe('Tick', this._tick);
    }
  }

  /** Destroy the ticker and don't use after this. Calling this method removes all references to internal events. */
  public destroy(): void {
    if (!this._protected) {
      this.stop();

      let listener = this._head?.next;

      while (listener) {
        listener = listener.destroy(true);
      }

      this._head?.destroy();
      this._head = null;
    }
  }

  public removeAllListeners() {
    let listener = this._head?.next;

    while (listener) {
      listener = listener.destroy(true);
    }
  }

  /**
   * Triggers an update. An update entails setting the
   * current {@link PIXI.Ticker#elapsedMS},
   * the current {@link PIXI.Ticker#deltaTime},
   * invoking all listeners with current deltaTime,
   * and then finally setting {@link PIXI.Ticker#lastTime}
   * with the value of currentTime that was provided.
   * This method will be called automatically by animation
   * frame callbacks if the ticker instance has been started
   * and listeners are added.
   * @param {number} [currentTime=performance.now()] - the current time of execution
   */
  public update(currentTime = performance.now()): void {
    let elapsedMS;

    // If the difference in time is zero or negative, we ignore most of the work done here.
    // If there is no valid difference, then should be no reason to let anyone know about it.
    // A zero delta, is exactly that, nothing should update.
    //
    // The difference in time can be negative, and no this does not mean time traveling.
    // This can be the result of a race condition between when an animation frame is requested
    // on the current JavaScript engine event loop, and when the ticker's start method is invoked
    // (which invokes the internal _requestIfNeeded method). If a frame is requested before
    // _requestIfNeeded is invoked, then the callback for the animation frame the ticker requests,
    // can receive a time argument that can be less than the lastTime value that was set within
    // _requestIfNeeded. This difference is in microseconds, but this is enough to cause problems.
    //
    // This check covers this browser engine timing issue, as well as if consumers pass an invalid
    // currentTime value. This may happen if consumers opt-out of the autoStart, and update themselves.

    if (currentTime > this.lastTime) {
      // Save uncapped elapsedMS for measurement
      elapsedMS = this.elapsedMS = currentTime - this.lastTime;

      // cap the milliseconds elapsed used for deltaTime
      if (elapsedMS > this._maxElapsedMS) {
        elapsedMS = this._maxElapsedMS;
      }

      elapsedMS *= this.speed;

      // If not enough time has passed, exit the function.
      // Get ready for next frame by setting _lastFrame, but based on _minElapsedMS
      // adjustment to ensure a relatively stable interval.
      if (this._minElapsedMS) {
        const delta = (currentTime - this._lastFrame) | 0;

        if (delta < this._minElapsedMS) {
          return;
        }

        this._lastFrame = currentTime - (delta % this._minElapsedMS);
      }

      this.deltaMS = elapsedMS;
      this.deltaTime = this.deltaMS * settings.TARGET_FPMS;

      // Cache a local reference, in-case ticker is destroyed
      // during the emit, we can still check for head.next
      const head = this._head;

      // Invoke listeners added to internal emitter
      let listener = head?.next;

      while (listener) {
        listener = listener.emit(this.deltaTime);
      }
    } else {
      this.deltaTime = this.deltaMS = this.elapsedMS = 0;
    }

    this.lastTime = currentTime;
  }

  /**
   * The frames per second at which this ticker is running.
   * The default is approximately 60 in most modern browsers.
   * **Note:** This does not factor in the value of
   * {@link PIXI.Ticker#speed}, which is specific
   * to scaling {@link PIXI.Ticker#deltaTime}.
   * @member {number}
   * @readonly
   */
  public get FPS(): number {
    return 1000 / this.elapsedMS;
  }

  /**
   * Manages the maximum amount of milliseconds allowed to
   * elapse between invoking {@link PIXI.Ticker#update}.
   * This value is used to cap {@link PIXI.Ticker#deltaTime},
   * but does not effect the measured value of {@link PIXI.Ticker#FPS}.
   * When setting this property it is clamped to a value between
   * `0` and `PIXI.settings.TARGET_FPMS * 1000`.
   * @member {number}
   * @default 10
   */
  public get minFPS(): number {
    return 1000 / this._maxElapsedMS;
  }

  public set minFPS(fps: number) {
    // Minimum must be below the maxFPS
    const minFPS = Math.min(this.maxFPS, fps);

    // Must be at least 0, but below 1 / settings.TARGET_FPMS
    const minFPMS = Math.min(Math.max(0, minFPS) / 1000, settings.TARGET_FPMS);

    this._maxElapsedMS = 1 / minFPMS;
  }

  /**
   * Manages the minimum amount of milliseconds required to
   * elapse between invoking {@link PIXI.Ticker#update}.
   * This will effect the measured value of {@link PIXI.Ticker#FPS}.
   * If it is set to `0`, then there is no limit; PixiJS will render as many frames as it can.
   * Otherwise it will be at least `minFPS`
   * @member {number}
   * @default 0
   */
  public get maxFPS(): number {
    if (this._minElapsedMS) {
      return Math.round(1000 / this._minElapsedMS);
    }

    return 0;
  }

  public set maxFPS(fps: number) {
    if (fps === 0) {
      this._minElapsedMS = 0;
    } else {
      // Max must be at least the minFPS
      const maxFPS = Math.max(this.minFPS, fps);

      this._minElapsedMS = 1 / (maxFPS / 1000);
    }
  }

  /**
   * The system ticker instance used by {@link PIXI.BasePrepare} for core timing
   * functionality that shouldn't usually need to be paused, unlike the `shared`
   * ticker which drives visual animations and rendering which may want to be paused.
   *
   * The property {@link PIXI.Ticker#autoStart} is set to `true` for this instance.
   * @member {PIXI.Ticker}
   * @static
   */
  public static get system(): Ticker {
    if (!Ticker._system) {
      const system = (Ticker._system = new Ticker());

      system.autoStart = false;
      system._protected = true;
    }

    return Ticker._system;
  }
}
